import React from "react";
import { PLACEMENTS, daysBetween } from "./adsConstants";

const CheckBox = ({ checked }) => (
  <span
    className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${
      checked ? "bg-[#095228]" : "border-2 border-[#c3c5d9]"
    }`}
  >
    {checked && (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
        <path d="M5 13l4 4L19 7" />
      </svg>
    )}
  </span>
);

export default function AdPlacement({ formData, updateFormData, onNext, onBack, continueLabel = "Continue", submitting = false }) {
  const coverImage = formData.mediaFiles[0]?.url;
  const days = daysBetween(formData.startDate, formData.endDate) || 1;
  const isDaily = formData.budgetType === "daily";
  const dailyAmount = isDaily ? Number(formData.budget) : Number(formData.budget) / days;
  const totalBudget = isDaily ? Number(formData.budget) * days : Number(formData.budget);

  const selectedPlacements = PLACEMENTS.filter((p) => formData.placements[p.key]);
  // Placement is optional, same as the promote-listing flow's targeting step - an ad can be
  // created with zero placements selected, it just won't get a delivery_config (so it stays
  // out of the buyer-facing sponsored auction until placements are added later).

  const togglePlacement = (key) => {
    updateFormData({ placements: { ...formData.placements, [key]: !formData.placements[key] } });
  };

  return (
    <div>
      {/* Figma "Ad Placement" screen (read-only access) - same 3 placement options and
          copy as the promote-listing flow's targeting step, just presented as its own
          screen here since the general flow has no combined targeting+placement step. */}
      <div className="flex items-start gap-4 mb-6">
        <div className="flex-1">
          <h2 className="text-lg font-bold text-black mb-1">Where Should Your Product Appear?</h2>
          <p className="text-sm text-[#747474]">
            Select the placements where you want to show your Product as a Sponsored Listing.
          </p>
        </div>
        {coverImage && (
          formData.mediaFiles[0]?.media_type === "video" ? (
            <video src={coverImage} controls className="w-20 h-20 object-cover rounded-xl shrink-0" />
          ) : (
            <img src={coverImage} alt="" className="w-20 h-20 object-cover rounded-xl shrink-0" />
          )
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <p className="text-sm font-semibold text-black">Daily budget</p>
          <p className="text-[#434656]">$ {dailyAmount.toFixed(2)} / Day</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-black">Schedule</p>
          <p className="text-[#434656]">
            {formData.startDate} - {formData.endDate}
          </p>
        </div>
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Select Placement</label>
      <div className="space-y-3 mb-6">
        {PLACEMENTS.map((p) => (
          <label
            key={p.key}
            className="flex items-start gap-3 bg-[#fefdeb] border border-[rgba(195,197,217,0.6)] rounded-2xl p-4 cursor-pointer"
          >
            <div className="mt-0.5" onClick={() => togglePlacement(p.key)}>
              <CheckBox checked={!!formData.placements[p.key]} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#131b2e]">{p.label}</span>
                {p.badge && (
                  <span className="bg-[#eef000] text-[10px] font-bold px-2 py-0.5 rounded-full">{p.badge}</span>
                )}
              </div>
              <p className="text-xs text-[#747474] mt-0.5">{p.description}</p>
            </div>
          </label>
        ))}
      </div>

      {selectedPlacements.length > 0 && (
        <>
          <label className="block text-sm font-semibold text-black mb-2">Selected Placements</label>
          <div className="flex gap-2 flex-wrap mb-6">
            {selectedPlacements.map((p) => (
              <span
                key={p.key}
                className="flex items-center gap-2 border border-black rounded-full pl-4 pr-1.5 py-1.5 text-xs font-bold uppercase"
              >
                {p.label}
                <button
                  type="button"
                  onClick={() => togglePlacement(p.key)}
                  className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </span>
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div>
          <p className="text-sm font-semibold text-black">Total Budget</p>
          <p className="text-[#434656]">$ {totalBudget.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-black">Daily Budget</p>
          <p className="text-[#434656]">$ {dailyAmount.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-black">Duration</p>
          <p className="text-[#434656]">{days} Days</p>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="border border-[#c3c5d9] text-[#434656] font-medium text-lg px-8 py-3 rounded-full"
        >
          Back
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={onNext}
          className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)] disabled:opacity-50"
        >
          {submitting ? "Saving..." : continueLabel}
        </button>
      </div>
    </div>
  );
}
